<template>
  <div class="full-recent-content">
    <!-- Estados de carga/error -->
    <div v-if="loading" class="loading-message typo-body-2">
      Cargando contenido reciente...
    </div>
    <div v-else-if="error" class="error-message typo-body-2">
      {{ error }}
    </div>
    
    <div v-else>
      <ul v-if="recentTopics.length" class="recent-topics-list">
        <li v-for="topic in recentTopics" :key="topic.nid" class="recent-content-item">
          <div class="topic-wrapper">
            <router-link 
              :to="{ name: 'ForumThread', params: { nid: topic.nid } }" 
              class="topic-link typo-body-2"
            >
              <i v-if="topic.isPinned" class="material-icons pin-icon" title="Hilo fijado">push_pin</i>
              {{ topic.title }}
              <div class="topic-meta">
                <span class="author">{{ topic.name }}</span>
                <span class="separator">•</span>
                <span class="last-update">Fecha de último envío: {{ formatDate(topic.last_comment_timestamp) }}</span>
                <span class="separator">•</span>
                <span class="comments-count">{{ topic.comment_count }} comentarios</span>
                <!-- Display new count if available and greater than 0 -->
                <span v-if="topic.new_count > 0" class="new-indicator">
                  <span class="separator">•</span>
                  <span class="new-count">{{ topic.new_count }} nuevo{{ topic.new_count > 1 ? 's' : '' }}</span>
                </span>
              </div>
            </router-link>

            <div class="topic-actions">
              <!-- Last Message Button -->
              <button 
                class="last-message-btn typo-caption" 
                @click.stop="goToLastMessage(topic)"
                title="Ir al último mensaje"
              >
                Último mensaje
              </button>

              <!-- Admin Button: Visible only if isAdmin is true -->
              <button 
                v-if="isAdmin" 
                @click.stop.prevent="toggleNegativeFlag(topic)" 
                class="admin-flag-btn"
                title="Marcar como negativo (ocultar de recientes)"
              >
                Eliminar de contenido reciente
              </button>

              <!-- NEW: Admin Pin/Unpin Button -->
              <button
                v-if="isAdmin"
                @click.stop.prevent="togglePin(topic)"
                class="admin-pin-btn"
                :class="{ pinned: topic.isPinned }"
                :title="topic.isPinned ? 'Desfijar hilo (ya fijado)' : 'Fijar hilo en contenido reciente'"
              >
                <i class="material-icons" style="margin-right:4px">push_pin</i>
                {{ topic.isPinned ? 'Desfijar' : 'Fijar' }}
              </button>
            </div>
          </div>
        </li>
      </ul>
      
      <!-- Mensaje si no hay temas -->
      <p v-else class="typo-body-2 no-topics-message">
        No hay temas recientes para mostrar.
      </p>
      
      <!-- Paginación -->
      <!-- <div class="pagination-controls" v-if="totalPages > 1">
        <button 
          class="pagination-button" 
          :disabled="currentPage === 1"
          @click="changePage(currentPage - 1)"
        >
          <i class="material-icons">chevron_left</i>
        </button>
        
        <span class="page-indicator typo-body-2">
          Página {{ currentPage }} de {{ totalPages }}
        </span>
        
        <button 
          class="pagination-button" 
          :disabled="currentPage === totalPages"
          @click="changePage(currentPage + 1)"
        >
          <i class="material-icons">chevron_right</i>
        </button>
      </div> -->
    </div>
  </div>
</template>

<script>
import forumApi from '@/services/forumApi';
import { mapGetters } from 'vuex';

const axios = forumApi;

export default {
  name: 'FullRecentContent',
  data() {
    return {
      recentTopics: [],
      loading: true,
      error: null,
      currentPage: 1,
      totalPages: 1,
      itemsPerPage: 12,
      pinnedTopics: [],
    };
  },
  computed: {
    ...mapGetters(['_g_IsUserLogged', '_g_User']),
    isAdmin() {
      return !!(this._g_User && this._g_User.is_admin_user);
    }
  },
  mounted() {
    // Load pinned topics first, then recent content
    this.fetchPinnedTopics().finally(() => {
      this.fetchRecentContent();
    });
  },
  methods: {
    fetchPinnedTopics() {
      this.pinnedTopics = [];
      return axios
        .get('/forum/api/pinned-topics')
        .then(res => {
          if (Array.isArray(res.data)) {
            this.pinnedTopics = res.data.map(t => ({ ...t, isPinned: true }));
          }
        })
        .catch(err => {
          console.error('Error fetching pinned topics:', err);
        });
    },
    mergePinnedTopics() {
      // Use string values for IDs to avoid type mismatches (e.g. '123' vs 123)
      const pinnedIds = new Set(this.pinnedTopics.map(t => String(t.nid)));
      // Mark pin state and build merged list
      const merged = [];
      
      // Add all pinned topics first
      this.pinnedTopics.forEach(pt => {
        pt.isPinned = true;
        merged.push(pt);
      });
      
      // Add recent topics that are NOT pinned
      (this.recentTopics || []).forEach(rt => {
        // Only add if not already in pinned list AND not already marked as pinned
        if (!pinnedIds.has(String(rt.nid)) && !rt.isPinned) {
          rt.isPinned = false;
          merged.push(rt);
        }
      });
      
      this.recentTopics = merged;
    },
    fetchRecentContent(page = 1) {
      this.loading = true;
      this.error = null;
      
      axios
        .get(`/forum/contenido-reciente?page=${page}&limit=${this.itemsPerPage}`)
        .then(response => {
          if (response.data && response.data.items) {
            this.recentTopics = response.data.items;
            this.totalPages = response.data.totalPages || 1;
            this.currentPage = response.data.currentPage || 1;
            // After fetching recent, merge with pinned
            this.mergePinnedTopics();
          } else if (Array.isArray(response.data)) {
            this.recentTopics = response.data;
            this.totalPages = 1;
            this.currentPage = 1;
            this.mergePinnedTopics();
          } else {
            // unexpected format – leave error handler
            this.error = 'Formato de respuesta no válido.';
            this.recentTopics = [];
          }
        })
        .catch(err => {
          console.error('Error fetching recent content:', err);
          this.error = 'Error al cargar el contenido reciente: ' + (err.response?.data?.message || err.message || 'Error desconocido');
          this.recentTopics = [];
        })
        .finally(() => {
          this.loading = false;
        });
    },
    changePage(page) {
      if (page >= 1 && page <= this.totalPages) {
        this.fetchRecentContent(page);
      }
    },
    async toggleNegativeFlag(topic) {
      if (!this.isAdmin) return;
      if (!topic || !topic.nid) {
        console.error('Invalid topic data for flagging:', topic);
        return;
      }

      const nid = topic.nid;
      const apiUrl = `/forum/api/flag/toggle/negative_forum/${nid}`;
      
      try {
        const response = await axios.post(apiUrl);
        
        if (response.data && response.data.success) {
          // console.log(`Topic ${nid} flag toggled:`, response.data.message);
          if (response.data.isFlagged) {
            this.recentTopics = this.recentTopics.filter(t => t.nid !== nid);
          }
        } else {
          throw new Error(response.data?.message || 'Failed to toggle flag');
        }
      } catch (err) {
        console.error(`Error toggling flag for topic ${nid}:`, err);
        this.error = `Error al marcar el tema ${nid}: ${err.response?.data?.message || err.message || 'Error desconocido'}`;
      }
    },
    async togglePin(topic) {
      if (!this.isAdmin || !topic || !topic.nid) return;
      const nid = topic.nid;
      try {
        const res = await axios.post(`/forum/api/pin/toggle/${nid}`);
        if (res.data && res.data.success) {
          const isPinned = res.data.isPinned;
          topic.isPinned = isPinned;
          if (isPinned) {
            // Add to pinnedTopics if not present
            if (!this.pinnedTopics.find(t => t.nid === nid)) {
              this.pinnedTopics.push({ ...topic, isPinned: true });
            }
          } else {
            // Remove from pinnedTopics
            this.pinnedTopics = this.pinnedTopics.filter(t => t.nid !== nid);
          }
          // Re-merge to refresh order
          this.mergePinnedTopics();
        } else {
          throw new Error(res.data?.message || 'No se pudo cambiar estado de fijado');
        }
      } catch (err) {
        console.error(`Error toggling pin for topic ${nid}:`, err);
        this.error = `Error al fijar/desfijar el tema ${nid}: ${err.response?.data?.message || err.message || 'Error desconocido'}`;
      }
    },
    goToLastMessage(topic) {
      // Navigate to the thread with the query parameter to go to the last message
      this.$router.push({ 
        name: 'ForumThread', 
        params: { nid: topic.nid },
        query: { last: 'true' }
      });
    },
    formatDate(dateValue) {
      // Handle null or undefined
      if (dateValue === null || dateValue === undefined) {
        return 'Fecha desconocida';
      }
      
      // If it's a number (Unix timestamp)
      if (typeof dateValue === 'number') {
        // Convert Unix timestamp (seconds) to JavaScript Date (milliseconds)
        const date = new Date(dateValue * 1000);
        return date.toLocaleString();
      } 
      // If it's a string that looks like a number
      else if (typeof dateValue === 'string' && /^\d+$/.test(dateValue)) {
        // Convert string timestamp to number
        const timestamp = parseInt(dateValue, 10);
        const date = new Date(timestamp * 1000);
        return date.toLocaleString();
      }
      // If it's already a date string
      else if (dateValue) {
        try {
          const date = new Date(dateValue);
          
          // Check if date is valid
          if (isNaN(date.getTime())) {
            return 'Fecha inválida';
          }
          
          return date.toLocaleString();
        } catch (e) {
          return 'Error de fecha';
        }
      }
      
      return 'Fecha desconocida';
    }
  }
};
</script>

<style scoped>
.full-recent-content {
  background-color: var(--color-surface);
  border-radius: 15px;
  padding: 20px;
  color: var(--color-text-primary);
}

/* Reset de lista */
ul {
  padding: 0;
  margin: 0;
}

.recent-content-item {
  margin-bottom: 12px;
  list-style-type: none;
  border-bottom: none;
  transition: transform 0.2s ease;
}

.recent-content-item:last-child {
  border-bottom: none;
}

.topic-wrapper {
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-bottom: 1px solid var(--color-outline);
  margin-bottom: 12px;
  padding-bottom: 12px;
  transition: transform 0.2s ease;
  flex-wrap: wrap;
  gap: 8px;
}

.recent-content-item:last-child .topic-wrapper {
  border-bottom: none;
  margin-bottom: 0;
  padding-bottom: 0;
}

.topic-link {
  flex-grow: 1;
  margin-right: 10px;
  color: var(--color-text-primary);
  text-decoration: none;
  display: block;
  padding: 12px 10px;
  border-radius: 8px;
  transition: all 0.3s ease;
  background-color: var(--color-surface-variant, rgba(0, 0, 0, 0.03));
}

.topic-link:hover {
  color: var(--color-highlight);
  background-color: var(--color-surface-hover, rgba(0, 0, 0, 0.05));
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

.topic-meta {
  display: flex;
  align-items: center;
  font-size: 0.85em;
  color: var(--color-text-secondary);
  margin-top: 4px;
  flex-wrap: wrap;
}

.separator {
  margin: 0 6px;
}

.author {
  font-style: italic;
}

.comments-count {
  white-space: nowrap;
}

.last-update {
  white-space: nowrap;
}

.new-indicator {
  display: inline-flex;
  align-items: center;
}

.new-count {
  color: #f44336;
  font-weight: bold;
  white-space: nowrap;
}

.no-topics-message {
  text-align: center;
  padding: 15px;
  color: var(--color-text-primary);
}

/* Paginación */
.pagination-controls {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 15px;
  margin-top: 20px;
}

.pagination-button {
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: var(--color-surface);
  border: 1px solid var(--color-outline);
  border-radius: 4px;
  padding: 8px;
  cursor: pointer;
  transition: background-color 0.2s ease;
}

.pagination-button:not(:disabled):hover {
  background-color: var(--color-active-surface);
}

.pagination-button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.page-indicator {
  color: var(--color-text-secondary);
}

/* Mensajes de carga y error */
.loading-message,
.error-message {
  padding: 20px;
  text-align: center;
  margin: 20px 0;
  color: var(--color-text-primary);
}

.error-message {
  border-left: 4px solid #f44336;
}

/* Styles for the list item wrapper and button */
.admin-flag-btn {
  flex-shrink: 0;
  background: none;
  border: 1px solid var(--color-error, #f44336);
  color: var(--color-error, #f44336);
  cursor: pointer;
  padding: 8px 12px;
  margin-left: 10px;
  border-radius: 4px;
  font-size: 0.85em;
  font-weight: 500;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: background-color 0.2s ease, color 0.2s ease;
  white-space: nowrap;
}

.admin-flag-btn:hover {
  background-color: rgba(244, 67, 54, 0.1);
}

.admin-flag-btn i {
  font-size: 20px;
}

.admin-pin-btn {
  flex-shrink: 0;
  background: none;
  border: 1px solid var(--color-highlight);
  color: var(--color-highlight);
  cursor: pointer;
  padding: 8px 12px;
  margin-left: 10px;
  border-radius: 4px;
  font-size: 0.85em;
  font-weight: 500;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: background-color 0.2s ease, color 0.2s ease;
  white-space: nowrap;
}

.admin-pin-btn:hover {
  background-color: rgba(33, 150, 243, 0.1); /* primary color light */
}

.admin-pin-btn.pinned {
  background-color: rgba(33, 150, 243, 0.1);
}

.pin-icon {
  font-size: 18px;
  vertical-align: middle;
  margin-right: 4px;
  color: var(--color-highlight);
}

.topic-actions {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}

.last-message-btn {
  background-color: var(--color-background);
  color: var(--color-text-primary);
  border: none;
  padding: 5px 10px;
  border-radius: 4px;
  cursor: pointer;
  transition: background-color 0.3s ease;
  font-size: 0.85em;
  white-space: nowrap;
}

.last-message-btn:hover {
  background-color: var(--color-highlight);
  color: var(--color-on-highlight, white);
}
</style> 